import type {
  CodexSectionView,
  CodexGrammarRow,
  CodexSenseView,
  CodexExampleView,
  ConjugationChart,
} from "@love-irish/resource-codex-meta-components"
import type { Profile } from "@love-irish/types"
import { POS_GRAMMAR, POS_OPTIONS, VERB_PERSONS } from "./pos"
import { groupSensesByPos } from "./grouping"
import { paradigm, TENSES, type Person, type ConjugationForms } from "./irish_grammar"
import type { Entry, SenseEntry, ExampleEntry } from "./types"

// Rebuild the serialized `sections` the codex show template consumes (CodexSectionView[]) from the flat
// entries tree — the JS mirror of CodexEntryPresenter, so a live preview groups the same way a reload
// does. Senses are grouped by POS via the shared groupSensesByPos; grammar/conjugation are read from the
// group's first (representative) sense. Server-only linguistic derivations (the full conjugation
// paradigm, grammar shorthand, swung-dash example text, homograph/entity splits) are approximated — the
// real show page renders them from the presenter.

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

// A sense's flat grammar map ({ gender: "masculine", … }) into labelled rows, carrying the dictionary
// shorthand (short field code + value abbreviation) from POS_GRAMMAR so compact cards can show "gs.
// reatha" / "m." instead of the full label + value. Shared by both section builders below.
const rowsFor = (pos: string, grammar: Record<string, string> = {}): CodexGrammarRow[] => {
  const rows: CodexGrammarRow[] = []
  for (const field of POS_GRAMMAR[pos] ?? []) {
    const value = grammar[field.key]
    if (!value) continue
    const option = field.options?.find((o) => o.value === value)
    rows.push({
      key: field.key,
      label: field.label,
      short: field.short,
      value: option?.label ?? value,
      valueShort: option?.valueShort,
    })
  }
  return rows
}

// A sense's flat grammar record ({ gender: "masculine", … }) into labeled rows, using the same POS
// layout config the forms use so labels match. Keys with no value are dropped.
const grammarRows = (sense: SenseEntry): CodexGrammarRow[] => rowsFor(sense.partOfSpeech, sense.grammar ?? {})

/**
 * The full conjugation chart, GENERATED client-side by the shared Irish-grammar engine from the headword
 * lemma + conjugation class + stored overrides — so the editor preview and the show page render the same
 * chart without the server sending a per-record blob. Nil when nothing can be generated. Exported so both
 * surfaces (and a standalone tool) build it the same way.
 */
export const buildConjugationChart = (
  lemma: string,
  opts: { conjugationClass?: string | null; forms?: ConjugationForms; source?: string | null } = {}
): ConjugationChart | undefined => {
  if (!lemma) return undefined
  const tenses = TENSES.flatMap((tense) => {
    const forms = paradigm(lemma, tense, opts)
    const rows = VERB_PERSONS.filter((p) => forms[p.key as Person]).map((p) => ({
      person: p.key,
      label: p.label,
      form: forms[p.key as Person] as string,
    }))
    return rows.length ? [{ tense, label: cap(tense), forms: rows }] : []
  })
  return tenses.length ? { tenses, ...(opts.source ? { source: opts.source } : {}) } : undefined
}

export const sectionsFromEntries = (entries: Entry[]): CodexSectionView[] => {
  // Uncategorized senses (no POS yet) are in-progress drafts — omit them from the dictionary preview.
  const senses = entries.filter((e): e is SenseEntry => e.kind === "sense" && !!e.partOfSpeech)
  const examplesBySense = new Map<string, ExampleEntry[]>()
  for (const e of entries) {
    if (e.kind !== "example") continue
    const list = examplesBySense.get(e.parentId) ?? []
    list.push(e)
    examplesBySense.set(e.parentId, list)
  }

  // Display POS: an entity sense (entity_type set) is shown as a proper noun, though it's grammatically a
  // noun — so it groups under "proper_noun" while its grammar still comes from its raw POS (noun).
  const displayPos = (s: SenseEntry) => (s.entityType ? "proper_noun" : s.partOfSpeech)

  return groupSensesByPos(senses, displayPos).map(({ partOfSpeech: pos, senses: group }) => {
    const representative = group[0]
    return {
      partOfSpeech: pos,
      partOfSpeechShort: cap(pos.replace(/_/g, " ")),
      grammar: grammarRows(representative),
      // Emit the verb metadata (class + overrides) and let the render layer generate the chart — one
      // generation site, shared with the show page. Only verbs carry it; the template gates on a class.
      conjugationClass: pos === "verb" ? representative.grammar?.conjugation_class ?? null : null,
      conjugationForms: (representative.conjugationForms as Record<string, unknown>) ?? null,
      conjugationSource: pos === "verb" ? representative.conjugationSource ?? null : null,
      senses: group.map((sense) => ({
        id: sense.id,
        definition: sense.definition || null,
        translation: sense.translation || null,
        register: sense.register || null,
        examples: (examplesBySense.get(sense.id) ?? []).map((ex) => ({
          id: ex.id,
          text: ex.text,
          translation: ex.translation || null,
          // Pass the standard Profile straight through (no bespoke {username, level} shape).
          contributor: ex.contributor ?? undefined,
        })),
      })),
    }
  })
}

// --- Display grouping from the `senses` records (the cache-friendly path) ------------------------------
//
// `sectionsFromEntries` above rebuilds the preview from the EDITOR's flat entries (no server labels). This
// pair rebuilds the display `CodexSectionView[]` from the GraphQL `senses` records instead, so a card can
// read normalized records (typename + id) and Apollo merges by id. The server-derived labels (entity /
// domain / genre) ride along on each record; only the POS/homograph grouping and the swung-dash example
// text are derived here. Mirrors CodexEntryPresenter. Known gap vs the presenter: grammar-row shorthand
// (short/valueShort/description) comes from FieldSchema and isn't ported yet, so rows show label + value.

/** A sense record as selected from the GraphQL `senses` field (loose: JSON scalars stay untyped). */
export interface CodexSenseRecord {
  id: string
  partOfSpeech?: string | null
  definition?: string | null
  translation?: string | null
  register?: string | null
  entityType?: string | null
  entityLabel?: string | null
  domain?: string | null
  domainLabel?: string | null
  genre?: string | null
  genreLabel?: string | null
  homographIndex?: number | null
  grammar?: Record<string, string> | null
  conjugationForms?: ConjugationForms | null
  conjugationSource?: string | null
  examples?: Array<{
    id: string
    text?: string | null
    translation?: string | null
    audioUrl?: string | null
    canEdit?: boolean | null
    contributor?: Profile | null
  }> | null
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// Replace the headword occurrences in an example with the swung dash (~), like the presenter's swung_dash.
const swungDash = (text: string, headword: string) => {
  if (!text || !headword) return text
  return text.replace(new RegExp(`\\b${escapeRegExp(headword)}\\b`, "gi"), "~")
}


const exampleViewFromRecord = (
  ex: NonNullable<CodexSenseRecord["examples"]>[number],
  headword: string
): CodexExampleView => ({
  id: ex.id,
  full: ex.text ?? "",
  text: swungDash(ex.text ?? "", headword),
  translation: ex.translation ?? null,
  audioUrl: ex.audioUrl ?? null,
  editable: ex.canEdit ?? false,
  contributor: ex.contributor ?? undefined,
})

const senseViewFromRecord = (s: CodexSenseRecord, headword: string): CodexSenseView => ({
  id: s.id,
  definition: s.definition ?? null,
  translation: s.translation ?? null,
  register: s.register ?? null,
  entityType: s.entityType ?? null,
  entityLabel: s.entityLabel ?? null,
  domain: s.domain ?? null,
  domainLabel: s.domainLabel ?? null,
  genre: s.genre ?? null,
  genreLabel: s.genreLabel ?? null,
  examples: (s.examples ?? []).map((ex) => exampleViewFromRecord(ex, headword)),
})

// Display POS: an entity sense (entity_type set) shows as a proper noun though grammatically a noun.
const displayPosOf = (s: CodexSenseRecord) => (s.entityType ? "proper_noun" : s.partOfSpeech || "noun")
const posRank = (pos: string) => {
  const i = POS_OPTIONS.indexOf(pos as (typeof POS_OPTIONS)[number])
  return i === -1 ? POS_OPTIONS.length : i
}

/**
 * Rebuild `CodexSectionView[]` from the GraphQL `senses` records + the headword name. Groups by homograph
 * index when any sense declares one (rith¹/rith²), else by display POS (proper nouns split out); the
 * representative sense's raw POS drives the grammar rows + conjugation. Mirrors CodexEntryPresenter.
 */
export const sectionsFromSenses = (
  senses: CodexSenseRecord[] | null | undefined,
  headword: string
): CodexSectionView[] => {
  if (!senses?.length) return []
  const numbered = senses.some((s) => s.homographIndex != null)

  const entries: Array<{ homographIndex: number | null; group: CodexSenseRecord[] }> = []
  if (numbered) {
    const byIndex = new Map<number, CodexSenseRecord[]>()
    for (const s of senses) {
      const idx = s.homographIndex ?? 1
      if (!byIndex.has(idx)) byIndex.set(idx, [])
      byIndex.get(idx)!.push(s)
    }
    for (const idx of [...byIndex.keys()].sort((a, b) => a - b)) {
      entries.push({ homographIndex: idx, group: byIndex.get(idx)! })
    }
  } else {
    const byPos = new Map<string, CodexSenseRecord[]>()
    for (const s of senses) {
      const pos = displayPosOf(s)
      if (!byPos.has(pos)) byPos.set(pos, [])
      byPos.get(pos)!.push(s)
    }
    for (const pos of [...byPos.keys()].sort((a, b) => posRank(a) - posRank(b))) {
      entries.push({ homographIndex: null, group: byPos.get(pos)! })
    }
  }

  return entries.map(({ homographIndex, group }) => {
    const rep = group[0]
    const pos = displayPosOf(rep)
    const rawPos = rep.partOfSpeech || "noun" // grammar/conjugation use the real POS (a name is a noun)
    const conjugationClass = rawPos === "verb" ? rep.grammar?.conjugation_class ?? null : null
    return {
      homographIndex,
      partOfSpeech: pos,
      partOfSpeechShort: cap(pos.replace(/_/g, " ")),
      grammar: rowsFor(rawPos, rep.grammar ?? {}),
      conjugationClass,
      conjugationForms: (rep.conjugationForms as Record<string, unknown>) ?? null,
      conjugationSource: rawPos === "verb" ? rep.conjugationSource ?? null : null,
      // Pre-build the chart so the card resolves conjugation tokens ({{ present.i }}) like the show page.
      // buildConjugationChart resolves stored overrides first (irregulars), else generates (regulars).
      conjugation:
        rawPos === "verb"
          ? buildConjugationChart(headword, {
              conjugationClass,
              forms: rep.conjugationForms ?? undefined,
              source: rep.conjugationSource ?? undefined,
            }) ?? null
          : null,
      senses: group.map((s) => senseViewFromRecord(s, headword)),
    }
  })
}
